/**
 * Atomic Edu — product analytics (PostHog).
 *
 * Single source of truth for the PostHog client. Initialised once at
 * module import time (no React mount needed) so the `track()` helper
 * can be called from anywhere — including pre-React code paths like
 * the Stripe redirect handler.
 *
 * **Privacy posture (Ontario K-12 — student PII risk):**
 *   - All `<input>` / `<textarea>` content masked in session replay
 *     (PostHog default for new projects).
 *   - Autocapture does NOT transmit form values — only click targets
 *     and structural attributes (PostHog default).
 *   - Custom events fired from the app NEVER include lesson prompt
 *     text, teacher freeform notes, student names, or generated
 *     lesson markdown. We only pass structured non-PII metadata
 *     (subject, grade, output_type, etc.).
 *   - Sensitive panels can opt out completely by adding the
 *     `ph-no-capture` class to the wrapper element.
 *
 * Identification is driven by `<AnalyticsIdentify>` mounted inside
 * `<AuthProvider>` — it watches `useAuth().user` and calls
 * `posthog.identify(user_id, { email, subscription_tier, ... })`
 * once per session per user.
 */

import posthog from "posthog-js";
import { useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || "";
const POSTHOG_HOST =
  process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

let initialised = false;

/**
 * Initialise PostHog once. Safe to call multiple times — subsequent
 * calls are no-ops.  Returns true when initialisation succeeded
 * (i.e. the key is present), false otherwise.
 */
export function initAnalytics() {
  if (initialised) return true;
  if (!POSTHOG_KEY) {
    // No key configured — silently skip. Lets the app run in
    // environments without analytics (CI, local dev without .env).
    return false;
  }
  if (typeof window === "undefined") return false;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Capture autocapture clicks + pageviews on history changes.
    // The "2026-01-30" defaults snapshot turns on SPA pageview
    // tracking automatically, so we don't need to call
    // `posthog.capture("$pageview")` manually on every route change.
    defaults: "2026-01-30",
    autocapture: true,
    capture_pageview: true,
    // Explicitly enable $pageleave — PostHog's Web Analytics tab
    // uses these to calculate bounce rate and session duration.
    // Without it both metrics are imprecise.
    capture_pageleave: true,
    enable_heatmaps: true,
    // Session replay: default-masked inputs/text, on for all users
    // so we get coverage. PII is protected by the input mask + the
    // `ph-no-capture` opt-out on sensitive panels.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: ".ph-mask-text",
    },
    // Don't ping the server in CI / when running tests.
    loaded: (ph) => {
      if (process.env.NODE_ENV === "test") ph.opt_out_capturing();
    },
  });
  initialised = true;

  // Expose on window for browser-console debugging + PostHog Toolbar
  // compatibility. The PostHog Toolbar (Cmd-Shift-E in the dashboard)
  // looks for `window.posthog`. With the npm-module install pattern
  // this isn't set automatically, so set it explicitly.
  if (typeof window !== "undefined") {
    window.posthog = posthog;
  }
  return true;
}

// Initialise eagerly on import — keeps the API simple (callers don't
// have to remember to init). The `initAnalytics()` function is
// exported only so tests / SSR can call it explicitly.
initAnalytics();

/**
 * Fire a typed product event. Properties must be flat, non-PII,
 * non-freeform metadata. Safe payload examples:
 *   { output_type: "lesson", grade: "Grade 5", subject: "Mathematics" }
 *   { tier: "pro", source: "pricing_page" }
 * Forbidden payload examples (do NOT include):
 *   prompt text, lesson markdown, student names, IEP notes,
 *   teacher freeform notes, prompt suggestions.
 */
export function track(event, properties) {
  if (!initialised) return;
  try {
    posthog.capture(event, properties || {});
  } catch (e) {
    // Never let analytics break a teacher flow.
    if (process.env.NODE_ENV === "development") {
      console.warn("analytics track failed:", e);
    }
  }
}

/**
 * Reset the PostHog distinct_id on sign-out so the next anonymous
 * session doesn't get attributed to the previous teacher.
 */
export function resetAnalytics() {
  if (!initialised) return;
  try {
    posthog.reset();
  } catch (e) {
    // No-op.
  }
}

/** Direct access for advanced use (feature flags, etc.). */
export { posthog };


/**
 * Mount this component inside `<AuthProvider>`. It watches the
 * current user and calls `posthog.identify()` with the backend
 * `user_id` once we know who they are. No UI is rendered.
 */
export function AnalyticsIdentify() {
  const { user } = useAuth();

  useEffect(() => {
    if (!initialised) return;
    if (!user || !user.id) return;
    try {
      posthog.identify(String(user.id), {
        email: user.email || undefined,
        subscription_tier: user.subscription_tier || "none",
        subscription_status: user.subscription_status || "inactive",
        is_admin: !!user.is_admin,
        has_math_engine_access: !!user.has_math_engine_access,
      });
    } catch (e) {
      // No-op — analytics never blocks the app.
    }
  }, [
    user?.id,
    user?.email,
    user?.subscription_tier,
    user?.subscription_status,
    user?.is_admin,
    user?.has_math_engine_access,
  ]);

  return null;
}
